import { db } from '../db';
import { ContactsRepo } from '../repos/contactsRepo';
import { queueForSync } from './syncOutbox';
import { buildContactSyncPayload, type ContactSyncPayload } from '../personnel/contactSync';

export type ContactSnapshot = {
  name: string;
  phone?: string;
  email?: string;
};

export type ContactConflict = {
  id: string;
  contactId: string;
  companyId: string;
  siteId?: string;
  local: ContactSnapshot;
  remote: ContactSnapshot;
  remoteUpdatedAt?: string | null;
  createdAt: string;
};

const CONTACT_CONFLICTS_KEY = 'sync:contactConflicts';

function normalizeConflicts(value: unknown): ContactConflict[] {
  if (!Array.isArray(value)) return [];
  return value.filter(Boolean) as ContactConflict[];
}

export async function getContactConflicts(): Promise<ContactConflict[]> {
  const entry = await db.meta.get(CONTACT_CONFLICTS_KEY);
  return normalizeConflicts(entry?.value);
}

async function saveContactConflicts(conflicts: ContactConflict[]): Promise<void> {
  await db.meta.put({ key: CONTACT_CONFLICTS_KEY, value: conflicts });
}

export async function recordContactConflict(conflict: ContactConflict): Promise<void> {
  const conflicts = await getContactConflicts();
  const next = [conflict, ...conflicts.filter((entry) => entry.contactId !== conflict.contactId)];
  await saveContactConflicts(next);
}

export async function removeContactConflict(contactId: string): Promise<void> {
  const conflicts = await getContactConflicts();
  await saveContactConflicts(conflicts.filter((entry) => entry.contactId !== contactId));
}

export async function resolveContactConflict(
  conflict: ContactConflict,
  resolution: 'keep-local' | 'keep-server'
): Promise<void> {
  if (resolution === 'keep-server') {
    const nameParts = conflict.remote.name.trim().split(' ').filter(Boolean);
    const lastName = nameParts.length > 1 ? (nameParts.at(-1) ?? '') : '';
    const firstName =
      nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : conflict.remote.name.trim();
    await ContactsRepo.applyContactSyncUpdate(conflict.contactId, {
      firstName,
      lastName,
      phone: conflict.remote.phone,
      email: conflict.remote.email,
      updatedAt: conflict.remoteUpdatedAt ?? new Date().toISOString(),
    });
    await removeContactConflict(conflict.contactId);
    return;
  }

  const local = await ContactsRepo.get(conflict.contactId);

  if (!local) {
    await removeContactConflict(conflict.contactId);
    return;
  }

  const payload: ContactSyncPayload = buildContactSyncPayload(
    conflict.companyId,
    local,
    conflict.remoteUpdatedAt ?? null
  );

  await queueForSync('contacts', conflict.contactId, 'upsert', payload);
  await removeContactConflict(conflict.contactId);
}
